/**
 * SourcesPanel V2 - MongoDB-Backed, Truthful Badges
 *
 * Uses actual processing status from backend:
 * - status: queued, uploading, staged, scanning, extracting, ocr_processing,
 *   chunking, importing, indexing, search_warming, ready, failed
 * - extractionProvider: local_pdf, local_docx, google_vision, openai_vision, etc.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  FileText,
  FileUp,
  Trash2,
  Plus,
  Search,
  MoreVertical,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Eye,
} from 'lucide-react';

import ideasKitchenApi from '@/pages/ideas-kitchen/api/ideasKitchenApi';
import PipelineStatusBadge from '@/pages/ideas-kitchen/components/PipelineStatusBadge';

const PROCESSING_STATUSES = new Set([
  'queued',
  'uploading',
  'staged',
  'scanning',
  'extracting',
  'ocr_processing',
  'chunking',
  'importing',
  'indexing',
  'search_warming',
  // Legacy compatibility:
  'pending',
  'processing',
  'ocr_queued',
  'chunked',
]);

const isSourceProcessing = source => {
  if (!source) return false;
  if (typeof source.isProcessing === 'boolean') return source.isProcessing;
  return PROCESSING_STATUSES.has(String(source.status || '').toLowerCase());
};

const normalizeSourceIdentity = source => {
  if (!source || typeof source !== 'object') return null;
  const id = source.id || source.sourceId || source._id || null;
  if (!id) return null;
  return {
    ...source,
    id: String(id),
    sourceId: source.sourceId || String(id),
  };
};

const SourcesPanel = ({ notebook, onFileSelect, selectedFile, onDelete, onRefresh }) => {
  const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 min (large files + slow networks)
  const [uploadItems, setUploadItems] = useState([]); // local queue for per-file UX
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [polledSourcesById, setPolledSourcesById] = useState({});
  const fileInputRef = useRef(null);
  const uploadItemsRef = useRef([]);
  const uploadWorkerRunningRef = useRef(false);
  const refreshTimerRef = useRef(null);

  const notebookSourcesFromProps = Array.isArray(notebook?.sources)
    ? notebook.sources
        .map(source => normalizeSourceIdentity(source))
        .filter(Boolean)
        .map(source => {
          const polled = polledSourcesById[source.id];
          if (!polled) return source;
          return {
            ...source,
            ...polled,
            id: source.id,
            sourceId: polled.sourceId || source.sourceId || source.id,
          };
        })
    : [];
  const notebookSourceIdsFromProps = new Set(notebookSourcesFromProps.map(s => s.id));
  const polledOnlySources = Object.values(polledSourcesById).filter(
    source => !notebookSourceIdsFromProps.has(source.id)
  );
  const notebookSources = [...polledOnlySources, ...notebookSourcesFromProps];
  const notebookSourceIds = new Set((notebookSources || []).map(s => s?.id).filter(Boolean));

  // Merge optimistic (local) sources into list, without duplicating once backend refresh includes them.
  const optimisticSources = (uploadItems || [])
    .map(u => u?.source)
    .filter(Boolean)
    .filter(s => !notebookSourceIds.has(s.id));

  const sources = [...optimisticSources, ...notebookSources];
  const hasActiveUploads = (uploadItems || []).some(
    u => u?.localStatus === 'queued' || u?.localStatus === 'uploading'
  );

  // Count by status
  const readyCount = sources.filter(s => s.status === 'ready').length;
  const processingCount = sources.filter(s => isSourceProcessing(s)).length;
  const failedCount = sources.filter(s => s.status === 'failed').length;

  // Polling should be driven only by backend sources (avoid starting/stopping due to local queued uploads)
  const backendProcessingCount = notebookSources.filter(s => isSourceProcessing(s)).length;

  // Keep refs current for the upload worker loop.
  useEffect(() => {
    uploadItemsRef.current = uploadItems;
  }, [uploadItems]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setPolledSourcesById({});
  }, [notebook?.id]);

  const scheduleRefresh = useCallback(
    (delayMs = 800) => {
      if (!onRefresh) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        onRefresh?.();
      }, delayMs);
    },
    [onRefresh]
  );

  // Poll for updates while processing
  useEffect(() => {
    if (backendProcessingCount === 0 || !notebook?.id) return undefined;

    const POLL_INTERVAL_MS = 3000;
    const POLL_BACKOFF_ON_429 = 10000;
    const MAX_POLL_ATTEMPTS = 240; // 12 minutes hard cap

    let attempts = 0;
    let cancelled = false;
    let pollInterval = POLL_INTERVAL_MS;
    let timerId = null;
    setIsPolling(true);

    const scheduleNext = () => {
      if (cancelled) return;
      timerId = setTimeout(pollSourceStatus, pollInterval);
    };

    const pollSourceStatus = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const response = await ideasKitchenApi.getSourcesStatus(notebook.id);
        pollInterval = POLL_INTERVAL_MS;

        if (response?.sources) {
          const normalizedPolled = response.sources
            .map(source => normalizeSourceIdentity(source))
            .filter(Boolean);
          const nextPolledById = {};
          normalizedPolled.forEach(source => {
            nextPolledById[source.id] = source;
          });
          setPolledSourcesById(nextPolledById);

          const stillProcessing = normalizedPolled.some(s => isSourceProcessing(s));
          if (response.notebookStatus === 'ready' || !stillProcessing || attempts >= MAX_POLL_ATTEMPTS) {
            setIsPolling(false);
            onRefresh?.();
            return;
          }
        }
      } catch (err) {
        const statusCode = Number(err?.status || err?.statusCode || err?.response?.status || 0);
        const isRateLimited =
          statusCode === 429 ||
          err?.code === 'RATE_LIMIT_EXCEEDED' ||
          err?.error?.code === 'RATE_LIMIT_EXCEEDED';
        if (isRateLimited) {
          console.warn('[IK] Status polling rate limited, backing off...');
          pollInterval = POLL_BACKOFF_ON_429;
        } else {
          pollInterval = POLL_INTERVAL_MS;
          console.warn('Polling error:', err);
        }
      }
      scheduleNext();
    };

    pollSourceStatus();

    return () => {
      cancelled = true;
      setIsPolling(false);
      if (timerId) clearTimeout(timerId);
    };
  }, [backendProcessingCount, notebook?.id, onRefresh]);

  const extractErrorMessage = err => {
    if (!err) return 'Upload failed';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    if (err?.error && typeof err.error === 'string') return err.error;
    if (err?.response?.data?.message) return err.response.data.message;
    if (err?.response?.data?.error) return err.response.data.error;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Upload failed';
    }
  };

  const makeClientId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const startUploadWorkerIfNeeded = useCallback(() => {
    if (uploadWorkerRunningRef.current) return;
    if (!notebook?.id) return;

    uploadWorkerRunningRef.current = true;

    (async () => {
      while (true) {
        const current = uploadItemsRef.current || [];
        const next = current.find(u => u?.localStatus === 'queued');
        if (!next) break;

        const clientId = next.clientId;
        const file = next.file;
        if (!clientId || !file) {
          // Skip invalid entries
          setUploadItems(prev => (prev || []).filter(u => u?.clientId !== clientId));
          continue;
        }

        // Mark as uploading
        setUploadItems(prev =>
          (prev || []).map(u => {
            if (u?.clientId !== clientId) return u;
            return {
              ...u,
              localStatus: 'uploading',
              progressPct: 0,
              source: {
                ...u.source,
                status: 'uploading',
                uploadProgress: 0,
                errorMessage: null,
                errorCode: null,
              },
            };
          })
        );

        try {
          const res = await ideasKitchenApi.uploadFiles(notebook.id, file, {
            timeoutMs: UPLOAD_TIMEOUT_MS,
            onUploadProgress: evt => {
              const loaded = Number(evt?.loaded || 0);
              const total = Number(evt?.total || file.size || 0);
              const rawPct = total > 0 ? Math.round((loaded / total) * 100) : 0;
              const pct = Math.max(0, Math.min(99, rawPct)); // leave 100% for server ACK

              setUploadItems(prev =>
                (prev || []).map(u => {
                  if (u?.clientId !== clientId) return u;
                  if (u?.localStatus !== 'uploading') return u;
                  return {
                    ...u,
                    progressPct: pct,
                    source: { ...u.source, status: 'uploading', uploadProgress: pct },
                  };
                })
              );
            },
          });

          const acceptedSource = Array.isArray(res?.sources) ? res.sources[0] : null;
          const finalSource = acceptedSource
            ? { ...acceptedSource, uploadProgress: 100 }
            : {
                ...(next.source || {}),
                status: 'pending',
                uploadProgress: 100,
              };

          setUploadItems(prev =>
            (prev || []).map(u => {
              if (u?.clientId !== clientId) return u;
              return {
                ...u,
                localStatus: 'accepted',
                progressPct: 100,
                source: finalSource,
              };
            })
          );

          // Refresh notebook soon so other panels see the new source.
          scheduleRefresh(500);
        } catch (err) {
          const msg = extractErrorMessage(err);
          setUploadItems(prev =>
            (prev || []).map(u => {
              if (u?.clientId !== clientId) return u;
              return {
                ...u,
                localStatus: 'failed',
                progressPct: 0,
                source: {
                  ...(u.source || {}),
                  status: 'failed',
                  errorMessage: msg,
                  errorCode: 'ERR_UPLOAD_FAILED',
                  uploadProgress: 0,
                },
              };
            })
          );
        }
      }
    })().finally(() => {
      uploadWorkerRunningRef.current = false;
    });
  }, [notebook?.id, scheduleRefresh]);

  const enqueueUploads = useCallback(
    files => {
      const list = Array.isArray(files) ? files : [];
      if (!notebook?.id || list.length === 0) return;

      const newItems = list.map(file => {
        const clientId = makeClientId();
        const mimeType = file?.type || 'application/octet-stream';
        return {
          clientId,
          file,
          localStatus: 'queued',
          progressPct: 0,
          source: {
            id: `upload-${clientId}`,
            displayName: file?.name || 'Untitled',
            fileName: file?.name || 'Untitled',
            mimeType,
            sizeBytes: file?.size || 0,
            status: 'queued',
            extractionProvider: 'none',
            ocrProvider: 'none',
            chunkCount: 0,
            embeddingCount: 0,
            errorCode: null,
            errorMessage: null,
            uploadedAt: new Date().toISOString(),
            lastProgressAt: new Date().toISOString(),
          },
        };
      });

      setUploadItems(prev => {
        const next = [...newItems, ...(prev || [])];
        uploadItemsRef.current = next;
        return next;
      });
      startUploadWorkerIfNeeded();
    },
    [notebook?.id, startUploadWorkerIfNeeded]
  );

  const handleFileChange = e => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    enqueueUploads(files);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;

    enqueueUploads(files);
  };

  const handleRetrySource = useCallback(
    async sourceId => {
      if (!notebook?.id || !sourceId) return;
      try {
        await ideasKitchenApi.retrySource(notebook.id, sourceId);
        onRefresh?.();
      } catch (err) {
        console.error('Retry failed:', err);
      }
    },
    [notebook?.id, onRefresh]
  );

  // ✅ FileTypeIcon component - matches Intelligence Studio icons (independent implementation)
  const FileTypeIcon = ({ mimeType, fileName, className = 'h-4 w-4' }) => {
    const [imageError, setImageError] = useState(false);

    // Extract extension from fileName or mimeType
    const ext = fileName
      ? fileName.split('.').pop()?.toLowerCase() || ''
      : mimeType?.split('/').pop()?.split('.').pop()?.toLowerCase() || '';

    const VSCODE_ICONS_BASE =
      'https://raw.githubusercontent.com/vscode-icons/vscode-icons/master/icons';

    // VS Code Icons for Office documents
    const cdnIcons = {
      pdf: `${VSCODE_ICONS_BASE}/file_type_pdf.svg`,
      doc: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
      docx: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
      xls: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
      xlsx: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
      csv: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
      ppt: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
      pptx: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
    };

    const cdnIcon = cdnIcons[ext];

    // If we have a CDN icon and it hasn't failed, use it
    if (cdnIcon && !imageError) {
      return (
        <img
          src={cdnIcon}
          alt={`${ext} file`}
          className={className}
          style={{ objectFit: 'contain' }}
          onError={() => setImageError(true)}
        />
      );
    }

    // Fallback to colored badges for non-CDN icons or if CDN fails
    // PDF - Red accent
    if (ext === 'pdf' || mimeType?.includes('pdf')) {
      return (
        <div
          className={`${className} flex items-center justify-center rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400`}
        >
          <span className="text-[9px] font-bold">PDF</span>
        </div>
      );
    }

    // Word - Blue accent
    if (
      ext === 'doc' ||
      ext === 'docx' ||
      mimeType?.includes('word') ||
      mimeType?.includes('document')
    ) {
      return (
        <div
          className={`${className} flex items-center justify-center rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400`}
        >
          <span className="text-[9px] font-bold">DOC</span>
        </div>
      );
    }

    // Excel - Green accent
    if (
      ext === 'xls' ||
      ext === 'xlsx' ||
      ext === 'csv' ||
      mimeType?.includes('spreadsheet') ||
      mimeType?.includes('excel')
    ) {
      return (
        <div
          className={`${className} flex items-center justify-center rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400`}
        >
          <span className="text-[9px] font-bold">XLS</span>
        </div>
      );
    }

    // PowerPoint - Orange accent
    if (
      ext === 'ppt' ||
      ext === 'pptx' ||
      mimeType?.includes('powerpoint') ||
      mimeType?.includes('presentation')
    ) {
      return (
        <div
          className={`${className} flex items-center justify-center rounded bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400`}
        >
          <span className="text-[9px] font-bold">PPT</span>
        </div>
      );
    }

    // Image files - Amber accent
    if (
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(ext) ||
      mimeType?.includes('image')
    ) {
      return (
        <div
          className={`${className} flex items-center justify-center rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400`}
        >
          <span className="text-[9px] font-bold">IMG</span>
        </div>
      );
    }

    // Text files - Gray accent
    if (ext === 'txt' || ext === 'md' || ext === 'rtf' || mimeType?.includes('text')) {
      return (
        <div
          className={`${className} flex items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400`}
        >
          <span className="text-[9px] font-bold">TXT</span>
        </div>
      );
    }

    // Default - Generic file
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400`}
      >
        <FileText className="h-3 w-3" />
      </div>
    );
  };

  const formatSize = bytes => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getSourceDisplayName = source =>
    source?.displayName || source?.fileName || source?.name || source?.originalName || 'Untitled';

  const filteredSources = sources.filter(source =>
    getSourceDisplayName(source).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-neutral-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 dark:text-white text-sm">Sources</h3>
            {isPolling && <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />}
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!notebook?.id}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            title="Upload files"
          >
            {hasActiveUploads ? (
              <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 text-slate-600 dark:text-neutral-400" />
            )}
          </button>
        </div>

        {/* Status Summary */}
        {sources.length > 0 && (
          <div className="flex items-center gap-2 mb-3 text-[10px]">
            {readyCount > 0 && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-emerald-700 dark:text-emerald-300">{readyCount} Ready</span>
              </div>
            )}
            {processingCount > 0 && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 rounded">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-blue-700 dark:text-blue-300">
                  {processingCount} Processing
                </span>
              </div>
            )}
            {failedCount > 0 && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-50 dark:bg-red-900/20 rounded">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span className="text-red-700 dark:text-red-300">{failedCount} Failed</span>
              </div>
            )}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search sources..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-slate-100 dark:bg-neutral-800 border-0 rounded-lg text-xs focus:ring-2 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf,.docx,.txt,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp"
      />

      {/* Sources List */}
      <div
        className="flex-1 overflow-y-auto p-2"
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
      >
        {filteredSources.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div className="p-3 bg-slate-100 dark:bg-neutral-800 rounded-xl mb-3">
              <FileUp className="h-6 w-6 text-slate-400 dark:text-neutral-500" />
            </div>
            <p className="text-sm text-slate-600 dark:text-neutral-400 mb-1">
              {sources.length === 0 ? 'No sources yet' : 'No matches'}
            </p>
            <p className="text-xs text-slate-400 dark:text-neutral-500">
              {sources.length === 0 ? 'Upload documents to get started' : 'Try a different search'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredSources.map((source, index) => {
              const sourceDisplayName = getSourceDisplayName(source);
              const isSelected = selectedFile?.id === source.id;
              const isProcessing = isSourceProcessing(source);
              const hasFailed = source.status === 'failed';
              const isUploadingLike = source.status === 'queued' || source.status === 'uploading';
              const hasServerId = typeof source.id === 'string' && !source.id.startsWith('upload-');
              const canOpenMenu = hasServerId && !isUploadingLike;
              const isSelectable = !isUploadingLike;

              return (
                <div
                  key={source.id || `${sourceDisplayName}-${index}`}
                  onClick={() => {
                    if (isSelectable) onFileSelect?.(source);
                  }}
                  className={`group relative flex items-center gap-2 p-2 rounded-lg transition-all ${
                    isSelected
                      ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800'
                      : `${isSelectable ? 'hover:bg-slate-100 dark:hover:bg-neutral-800 cursor-pointer' : 'cursor-default opacity-80'} border border-transparent`
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`relative flex-shrink-0 ${
                      isSelected
                        ? 'bg-amber-200 dark:bg-amber-800/50'
                        : 'bg-slate-100 dark:bg-neutral-800'
                    }`}
                  >
                    {isProcessing ? (
                      <div className="p-1.5 rounded-lg">
                        <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                      </div>
                    ) : hasFailed ? (
                      <div className="p-1.5 rounded-lg">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      </div>
                    ) : (
                      <FileTypeIcon
                        mimeType={source.mimeType}
                        fileName={source.displayName || source.fileName}
                        className="h-5 w-5"
                      />
                    )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-xs font-medium truncate ${
                        isSelected
                          ? 'text-amber-800 dark:text-amber-200'
                          : 'text-slate-700 dark:text-neutral-300'
                      }`}
                    >
                      {sourceDisplayName}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <PipelineStatusBadge
                        status={source.status}
                        failedAtStage={source.failedAtStage}
                        detail={source.detail || source.errorMessage}
                        errorCode={source.errorCode}
                        onRetry={
                          hasFailed && source.id
                            ? () => {
                                handleRetrySource(source.id);
                              }
                            : undefined
                        }
                        showDetail={true}
                      />
                      <span className="text-[10px] text-slate-400 dark:text-neutral-500">
                        {formatSize(source.sizeBytes)}
                      </span>
                      {source.checksumSha256 && (
                        <span
                          className="text-[10px] text-slate-400 dark:text-neutral-500"
                          title={`SHA-256: ${source.checksumSha256}`}
                        >
                          • sha:{source.checksumSha256.substring(0, 8)}
                        </span>
                      )}
                      {source.textLength > 0 && (
                        <span className="text-[10px] text-slate-400 dark:text-neutral-500">
                          • {(source.textLength / 1000).toFixed(0)}k chars
                        </span>
                      )}
                    </div>

                    {/* Per-file upload progress (local queue) */}
                    {(source.status === 'queued' || source.status === 'uploading') && (
                      <div className="mt-1">
                        <div className="h-1.5 w-full bg-slate-200 dark:bg-neutral-700 rounded">
                          <div
                            className="h-1.5 bg-amber-500 rounded"
                            style={{
                              width: `${Math.max(0, Math.min(99, Number(source.uploadProgress || 0)))}%`,
                            }}
                          />
                        </div>
                        <div className="mt-0.5 text-[10px] text-slate-500 dark:text-neutral-400">
                          {source.status === 'queued'
                            ? 'Queued…'
                            : `Uploading… ${Math.max(0, Math.min(99, Number(source.uploadProgress || 0)))}%`}
                        </div>
                      </div>
                    )}

                    {source.status === 'failed' && source.errorMessage && (
                      <div
                        className="mt-1 text-[10px] text-red-600 dark:text-red-400 truncate"
                        title={source.errorMessage}
                      >
                        {source.errorMessage}
                      </div>
                    )}
                  </div>

                  {/* Menu button */}
                  {canOpenMenu && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setMenuOpen(menuOpen === source.id ? null : source.id);
                      }}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded transition-all"
                    >
                      <MoreVertical className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  )}

                  {/* Dropdown menu */}
                  {canOpenMenu && menuOpen === source.id && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={e => {
                          e.stopPropagation();
                          setMenuOpen(null);
                        }}
                      />
                      <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-slate-200 dark:border-neutral-700 py-1 z-20">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onFileSelect?.(source);
                            setMenuOpen(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-neutral-700"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Preview
                        </button>

                        {hasFailed && (
                          <button
                            onClick={async e => {
                              e.stopPropagation();
                              try {
                                await handleRetrySource(source.id);
                              } catch (err) {
                                console.error('Retry failed:', err);
                              } finally {
                                setMenuOpen(null);
                              }
                            }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Retry
                          </button>
                        )}

                        <div className="border-t border-slate-100 dark:border-neutral-700 my-1" />

                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onDelete?.(source.id);
                            setMenuOpen(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SourcesPanel;
